/**
 * Statut Premium partagé (même logique que GameTime/PremiumContext) :
 * lecture temps réel de users/$uid/premium, admin permanent, tolérance de 5 min
 * après expiration pour laisser RevenueCat se synchroniser.
 */
import React, { createContext, useContext, useEffect, useState } from "react";
import { onAuthStateChanged } from "firebase/auth";
import { ref, onValue, off } from "firebase/database";
import { auth, db } from "../firebase";
import type { ChapterMeta } from "../content/types";

export interface PremiumInfo {
  active?: boolean;
  isAdmin?: boolean;
  type?: string | null;
  expirationDate?: string | number | null;
  willRenew?: boolean;
  productId?: string;
}

interface PremiumContextValue {
  isPremium: boolean;
  premiumInfo: PremiumInfo | null;
  loading: boolean;
  canAccessChapter: (meta: ChapterMeta) => boolean;
}

const PremiumContext = createContext<PremiumContextValue>({
  isPremium: false,
  premiumInfo: null,
  loading: true,
  canAccessChapter: () => false,
});

export const usePremium = () => useContext(PremiumContext);

export function isPremiumActive(p: PremiumInfo | null): boolean {
  if (!p) return false;
  if (p.isAdmin === true) return true;
  if (p.active !== true) return false;
  if (!p.expirationDate) return true;
  const exp = typeof p.expirationDate === "string" ? new Date(p.expirationDate).getTime() : p.expirationDate;
  return Date.now() < exp + 5 * 60 * 1000;
}

export const PremiumProvider: React.FC = ({ children }) => {
  const [isPremium, setIsPremium] = useState(false);
  const [premiumInfo, setPremiumInfo] = useState<PremiumInfo | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cleanupData: (() => void) | null = null;
    const unsubAuth = onAuthStateChanged(auth, (user) => {
      if (cleanupData) {
        cleanupData();
        cleanupData = null;
      }
      if (!user) {
        setIsPremium(false);
        setPremiumInfo(null);
        setLoading(false);
        return;
      }
      const r = ref(db, `users/${user.uid}/premium`);
      const handler = onValue(
        r,
        (snap) => {
          const data = (snap.val() as PremiumInfo | null) || null;
          setPremiumInfo(data);
          setIsPremium(isPremiumActive(data));
          setLoading(false);
        },
        () => setLoading(false)
      );
      cleanupData = () => off(r, "value", handler);
    });
    return () => {
      unsubAuth();
      if (cleanupData) cleanupData();
    };
  }, []);

  const canAccessChapter = (meta: ChapterMeta) => isPremium || meta.free;

  return (
    <PremiumContext.Provider value={{ isPremium, premiumInfo, loading, canAccessChapter }}>
      {children}
    </PremiumContext.Provider>
  );
};
